const BASE_SCHEMA = {
  findability: {
    max: 60,
    fields: [
      { name: "dcat:keyword", label: "dcatKeyword", max: 30 },
      { name: "dcat:theme", label: "dcatTheme", max: 30 },
    ],
  },
  accessibility: {
    max: 40,
    fields: [
      { name: "dcat:accessURL", label: "dcatAccessURL", max: 20 },
      { name: "dcat:downloadURL", label: "dcatDownloadURL", max: 20 },
    ],
  },
  interoperability: {
    max: 30,
    fields: [
      { name: "dcat:mediaType", label: "dcatMediaType", max: 10 },
      { name: "dct:format", label: "dctFormat", max: 20 },
    ],
  },
  reusability: {
    max: 40,
    fields: [
      { name: "dcat:contactPoint", label: "dcatContactPoint", max: 20 },
      { name: "dct:license", label: "dctLicense", max: 20 },
    ],
  },
  contextuality: {
    max: 10,
    fields: [
      { name: "dct:issued", label: "dctIssued", max: 5 },
      { name: "dct:modified", label: "dctModified", max: 5 },
    ],
  },
};

const MAX_TOTAL = 180;
const LEGACY_IGNORED_KEYS = new Set([
  "overallScore",
  "overall_score",
  "quality_score",
  "scores",
  "details",
  "dqv_report_ttl",
]);
const SCORE_REGEX = /(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/;
const KEY_MAP = {
  findability: "findability",
  Findability: "findability",
  accessibility: "accessibility",
  Accessibility: "accessibility",
  interoperability: "interoperability",
  Interoperability: "interoperability",
  reusability: "reusability",
  Reusability: "reusability",
  contextuality: "contextuality",
  Contextuality: "contextuality",
};

/**
 * @param {number} score
 * @param {number} maxScore
 * @returns {"pass" | "fail" | "warn"}
 */
function getStatus(score, maxScore) {
  if (score === maxScore && maxScore > 0) return "pass";
  if (score === 0) return "fail";
  return "warn";
}

/**
 * @param {number} score
 * @returns {"labelExcellent" | "labelGood" | "labelFair" | "labelPoor"}
 */
function getLabel(score) {
  if (score >= 140) return "labelExcellent";
  if (score >= 100) return "labelGood";
  if (score >= 60) return "labelFair";
  return "labelPoor";
}

function normalizeStatus(status, points, maxPoints) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "pass" || normalized === "ok") return "pass";
  if (normalized === "fail") return "fail";
  return getStatus(points, maxPoints);
}

function parsePointsFromMessage(message) {
  const msg = String(message || "");
  const match = msg.match(SCORE_REGEX);
  if (!match) {
    return { points: 0, maxPoints: 0 };
  }

  const points = Number(match[1]);
  const maxPoints = Number(match[2]);
  return {
    points: Number.isFinite(points) ? points : 0,
    maxPoints: Number.isFinite(maxPoints) ? maxPoints : 0,
  };
}

function parseFieldNameFromMessage(message) {
  const msg = String(message || "");
  const match = msg.match(/^(.*):\s*\d+(?:\.\d+)?\s*\/\s*\d+(?:\.\d+)?/);
  if (match?.[1]) return match[1].trim();
  return "";
}

function normalizeDimensionKey(key) {
  return KEY_MAP[key] || null;
}

/**
 * @param {object} qualityReportData -
 * @returns {object | null}
 */
export function transformQualityReport(qualityReportData) {
  const report = qualityReportData;
  const overallScore =
    report?.quality_score ??
    report?.overall_score ??
    report?.overallScore ??
    report?.score;

  if (!report || typeof overallScore === "undefined" || overallScore === null) {
    return null;
  }

  const transformedDetails = [];
  const transformedBreakdown = {};
  const scoresBlock = report?.scores || {};
  const detailsBlock = report?.details || {};

  const legacyDimensions = {};
  for (const [rawKey, value] of Object.entries(report || {})) {
    if (LEGACY_IGNORED_KEYS.has(rawKey)) continue;
    const key = normalizeDimensionKey(rawKey);
    if (!key) continue;
    legacyDimensions[key] = value;
  }

  for (const [key, baseDimension] of Object.entries(BASE_SCHEMA)) {
    const legacyDimension = legacyDimensions[key] || {};

    const scoreCandidates = [
      scoresBlock?.[key],
      scoresBlock?.[key[0].toUpperCase() + key.slice(1)],
      legacyDimension?.value,
    ];
    const dimensionScoreRaw = scoreCandidates.find(
      (v) => typeof v !== "undefined" && v !== null,
    );
    const dimensionScore = Number.isFinite(Number(dimensionScoreRaw))
      ? Number(dimensionScoreRaw)
      : 0;
    const dimensionMaxScore = baseDimension.max;

    const detailsCandidates = [
      detailsBlock?.[key],
      detailsBlock?.[key[0].toUpperCase() + key.slice(1)],
    ];
    const newDetailItems = detailsCandidates.find((arr) => Array.isArray(arr));

    const transformedItems = baseDimension.fields.map((baseField) => {
      let points = 0;
      let maxPoints = baseField.max;
      let status = "warn";

      if (Array.isArray(newDetailItems)) {
        const matching = newDetailItems.find((item) => {
          const fieldName = parseFieldNameFromMessage(item?.message);
          return fieldName === baseField.name;
        });

        if (matching) {
          const parsed = parsePointsFromMessage(matching?.message);
          points = parsed.points;
          maxPoints = parsed.maxPoints || baseField.max;
          status = normalizeStatus(matching?.status, points, maxPoints);
        } else {
          status = getStatus(points, maxPoints);
        }
      } else {
        const apiFieldScore =
          legacyDimension?.fields?.find((f) => f.name === baseField.name)
            ?.value ?? 0;
        points = Number(apiFieldScore) || 0;
        status = getStatus(points, maxPoints);
      }

      return {
        name: baseField.label,
        label: baseField.label,
        points,
        maxPoints,
        status,
      };
    });

    transformedBreakdown[key] = {
      score: dimensionScore,
      max: dimensionMaxScore,
    };

    transformedDetails.push({
      name: key,
      key,
      status: getStatus(dimensionScore, dimensionMaxScore),
      score: dimensionScore,
      maxScore: dimensionMaxScore,
      items: transformedItems,
    });
  }

  return {
    score: overallScore,
    maxScore: MAX_TOTAL,
    label: getLabel(overallScore),
    breakdown: transformedBreakdown,
    details: transformedDetails,
  };
}
